import { useLayoutEffect, useMemo, useRef, useState, type CSSProperties } from 'react'

import BarFill from '@/components/charts/BarFill'
import Reveal from '@/components/motion/Reveal'
import EmptyState from '@/components/ui/EmptyState'
import Skeleton from '@/components/ui/Skeleton'
import { bsDayMonth } from '@/features/trading-average/lib/tradingAverage'
import { sessionAveragePrice } from '@/features/trading-history/lib/tradingHistory'
import { formatBsDate } from '@/lib/bsDate'
import { formatMoney, formatQuantity } from '@/lib/format'
import type { Money } from '@/lib/money'
import { cn } from '@/lib/utils'
import type { PriceHistoryPoint } from '@/schemas/prices'

/** The plot's own box (the mock's `mkc-bars`): 188 tall, a 24 gutter each side, labels in the last 36. */
const PLOT_H = 188
const GUTTER = 24
const LABEL_ROOM = 36
const BAR_MIN = 30
const BAR_SPAN = 113
/** The least a session may take, so a long period scrolls sideways rather than crushing the bars. */
const SLOT_MIN = 14
const BAR_MAX = 52
/** Below this a label has no room beside its neighbour. */
const LABEL_SLOT = 72
const TIP_HALF = 70
/** The tip sits this far over the top of the bar it reads. */
const TIP_LIFT = 58

interface Bar {
  date: string
  avg: number
  money: Money
  shares: number | null
}

interface AverageBarsProps {
  /** Oldest first, as the series is served. */
  sessions: readonly PriceHistoryPoint[]
  symbol: string
}

/**
 * The container's width, kept current. 812 is the mock's own, held until the first measure.
 */
function useWidth() {
  const ref = useRef<HTMLDivElement>(null)
  const [width, setWidth] = useState(812)
  useLayoutEffect(() => {
    const node = ref.current
    if (node === null) return
    const measure = () => {
      const next = Math.floor(node.getBoundingClientRect().width)
      if (next > 0) setWidth(next)
    }
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(node)
    return () => {
      observer.disconnect()
    }
  }, [])
  return [ref, width] as const
}

/**
 * "Daily average price" as the mock draws it: one green bar per session across the period, on the
 * sheet fill, no axis. Bars are scaled between the period's lowest and highest average (30 to 143
 * tall), so the shape of the period reads and the figure is in the tip; a hover (or focus) on a
 * column brightens its bar and floats "Rs 884.30" over "27 Bhadra 2083 · 804 kitta" above it.
 *
 * A period too long for 14px a session scrolls sideways inside its card. Bars grow up from their
 * base, 50ms apart, and the date labels thin to every n-th so none touch.
 *
 * A session with no turnover or no shares has no average to draw (`sessionAveragePrice`), and is
 * left out rather than drawn at zero.
 */
export default function AverageBars({ sessions, symbol }: AverageBarsProps) {
  const [boxRef, boxWidth] = useWidth()
  const [active, setActive] = useState<number | null>(null)

  const bars = useMemo<Bar[]>(
    () =>
      sessions.flatMap((session) => {
        const money = sessionAveragePrice(session)
        return money === null
          ? []
          : [{ date: session.date, avg: Number(money), money, shares: session.volume }]
      }),
    [sessions],
  )

  const count = bars.length
  const plotW = Math.max(boxWidth, count * SLOT_MIN + GUTTER * 2)
  const overflowing = plotW > boxWidth
  const slot = count === 0 ? 0 : (plotW - GUTTER * 2) / count
  const barW = Math.min(BAR_MAX, slot * 0.62)
  const lowest = Math.min(...bars.map((bar) => bar.avg))
  const highest = Math.max(...bars.map((bar) => bar.avg))
  const heightOf = (avg: number) =>
    highest === lowest ? 100 : BAR_MIN + ((avg - lowest) / (highest - lowest)) * BAR_SPAN
  const labelEvery = Math.max(Math.ceil(count / 9), slot > 0 ? Math.ceil(LABEL_SLOT / slot) : 1)
  const focused = active === null ? undefined : bars[active]

  return (
    <section className="flex flex-col gap-2 bg-surface-sheet p-4" aria-label="Daily average price">
      <h2 className="text-h-s">Daily average price</h2>
      <div ref={boxRef}>
        {count === 0 ? (
          <EmptyState kind="empty" size="inline" title="No trades in this range." />
        ) : (
          <div
            className={cn(
              overflowing && 'scrollbar-slim -mt-15 overflow-x-auto overflow-y-hidden pt-15',
            )}
          >
            <div
              role="list"
              aria-label={`${symbol}: the price traded on average each session`}
              className="relative"
              style={{ height: PLOT_H, width: plotW }}
              onMouseLeave={() => {
                setActive(null)
              }}
            >
              {bars.map((bar, index) => {
                const center = GUTTER + slot * index + slot / 2
                const column: CSSProperties = { left: center - slot / 2, width: slot }
                return (
                  <div
                    key={bar.date}
                    role="listitem"
                    tabIndex={0}
                    aria-label={`${formatBsDate(bar.date)}: Rs ${formatMoney(bar.money)}${bar.shares === null ? '' : `, ${formatQuantity(bar.shares)} kitta`}`}
                    className="group/bar absolute inset-y-0 cursor-default focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring"
                    style={column}
                    onMouseEnter={() => {
                      setActive(index)
                    }}
                    onFocus={() => {
                      setActive(index)
                    }}
                    onBlur={() => {
                      setActive(null)
                    }}
                  >
                    <BarFill
                      as="span"
                      from="bottom"
                      index={index}
                      className={cn(
                        'absolute left-1/2 -translate-x-1/2 bg-market-up-fill transition-[filter] duration-fast',
                        active === index && 'brightness-118',
                      )}
                      style={{ bottom: LABEL_ROOM, width: barW, height: heightOf(bar.avg) }}
                    />
                    {index % labelEvery === 0 ? (
                      <Reveal
                        as="span"
                        variant="fade-in"
                        aria-hidden="true"
                        className="absolute top-40 left-1/2 -translate-x-1/2 text-body-xs whitespace-nowrap text-muted-foreground"
                      >
                        {bsDayMonth(bar.date)}
                      </Reveal>
                    ) : null}
                  </div>
                )
              })}
              {focused === undefined || active === null ? null : (
                <Reveal
                  variant="fade-in"
                  aria-hidden="true"
                  className="pointer-events-none absolute flex -translate-x-1/2 flex-col items-center gap-1 bg-tooltip p-2 whitespace-nowrap text-tooltip-foreground ring-1 ring-tooltip-border"
                  style={{
                    left: Math.min(
                      Math.max(GUTTER + slot * active + slot / 2, TIP_HALF),
                      plotW - TIP_HALF,
                    ),
                    top: PLOT_H - LABEL_ROOM - heightOf(focused.avg) - TIP_LIFT,
                  }}
                >
                  <span className="text-h-xs">Rs {formatMoney(focused.money)}</span>
                  <span className="text-body-xs text-muted-foreground-subtle">
                    {formatBsDate(focused.date)}
                    {focused.shares === null ? '' : ` · ${formatQuantity(focused.shares)} kitta`}
                  </span>
                </Reveal>
              )}
            </div>
          </div>
        )}
      </div>
    </section>
  )
}

/** The card's box, so nothing moves when the bars land. */
export function AverageBarsSkeleton() {
  return (
    <section className="flex flex-col gap-2 bg-surface-sheet p-4" aria-hidden="true">
      <h2 className="text-h-s">&nbsp;</h2>
      <Skeleton className="h-47 w-full" />
    </section>
  )
}
